import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BillingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const maxAttempts = 5;

  const checkStatus = useCallback(async (attemptCount = 0) => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      return;
    }

    try {
      const res = await axios.get(
        `${API_URL}/api/billing/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        toast.success('Subscription activated!');
      } else if (res.data.status === 'expired') {
        setStatus('error');
      } else if (attemptCount < maxAttempts) {
        setTimeout(() => checkStatus(attemptCount + 1), 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error('Status check error:', error);
      if (attemptCount < maxAttempts) {
        setTimeout(() => checkStatus(attemptCount + 1), 2000);
      } else {
        setStatus('error');
      }
    }
  }, [searchParams, token, refreshUser]);

  useEffect(() => {
    checkStatus(0);
  }, [checkStatus]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="billing-success">
      <Card className="card-farm max-w-md w-full">
        <CardContent className="py-12 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto animate-spin text-primary mb-6" />
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                Processing Payment
              </h1>
              <p className="text-muted-foreground">
                Please wait while we confirm your subscription...
              </p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center mb-6">
                <Check className="w-8 h-8 text-primary" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                Welcome to Fencepost!
              </h1>
              <p className="text-muted-foreground mb-6">
                Your subscription is now active. You have full access to all features.
              </p>
              <Button 
                onClick={() => navigate('/app')}
                className="btn-primary"
                data-testid="go-to-app-btn"
              >
                Go to Dashboard
              </Button>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto rounded-full bg-destructive/20 flex items-center justify-center mb-6">
                <span className="text-3xl">!</span>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
                Something Went Wrong
              </h1>
              <p className="text-muted-foreground mb-6">
                We couldn't confirm your payment. Please try again or contact support.
              </p>
              <div className="flex gap-3 justify-center">
                <Button 
                  variant="outline"
                  onClick={() => navigate('/app/settings')}
                >
                  Go to Settings
                </Button>
                <Button 
                  onClick={() => navigate('/app')}
                  className="btn-primary"
                >
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingSuccess;
